import { atom, atomFamily, selector, selectorFamily } from "recoil";
import { getProperty } from "../utils/FPEUtils";
import { savedDisplayOperationState } from "../hooks/useDisplayOperation";
import { weatherVisibleWithID, WX_ID_LIVECAMERA } from "./WeatherContentsState";
import { userState } from "./UserState";
import axios from "axios";
import { executeLinksAPI } from "../utils/LinksAPI";
import { useCallback } from "react";
import { COMMUNICATE_ERROR_MESSAGE_LIVECAMERA_GROUP, COMMUNICATE_ERROR_MESSAGE_SELECTABLE_LIST, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD } from "../constants/ErrorMessage";

/**
 * カメラグループリスト（プリセット）（フィルタリング用）
 */
const presetCameraGroupListState = atom({
  key: 'presetCameraGroupListState',
  default: [
    {
      id: 'preset1',
      label: 'Tougenochaya',
      category: ['SKY'],
      filterType: 'category'
    },
    {
      id: 'preset2',
      label: 'Supporter',
      category: ['public'],
      filterType: 'category'
    },
    {
      id: 'preset3',
      label: 'WNI',
      category: ['LAND', 'internal', 'external'],
      filterType: 'category'
    }
  ]
});

/**
 * カメラ並び順（first-key: prefecture_code, second-key:area）
 */
const cameraSort = (a, b) => {
  if (a.prefecture_code === '' && b.prefecture_code !== '') {
    return 1;
  } else if (a.prefecture_code !== '' && b.prefecture_code === '') {
    return -1;
  }
  const c = a.prefecture_code.localeCompare(b.prefecture_code);
  if (c !== 0) {
    return c;
  }
  return a.area.localeCompare(b.area);
};

/**
 * カメラグループリスト(ユーザー保存)（フィルタリング用）
 */
export const cameraGroupListQuery = selector({
  key: 'cameraGroupListQuery',
  get: async () => {
    return await executeLinksAPI('fosterlinks/livecamera_group', {
      method: 'get',
      errorHandle: {
        message: COMMUNICATE_ERROR_MESSAGE_LIVECAMERA_GROUP,
        additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
      }
    }).then(data => data).catch(e => {
      console.log('camera_group_list get failed.');
      return [];
      // throw e;
    });
  }
});

/**
 * カメラグループリスト(プリセット+ユーザー保存)（フィルタリング用）
 */
export const cameraGroupListSelector = selector({
  key: 'cameraGroupListSelector',
  get: ({get}) => {
    const preset = get(presetCameraGroupListState);
    const userCameraGroupList = get(cameraGroupListQuery);
    return [
      ...preset,
      ...userCameraGroupList.map(cameraGroup => ({
        id: cameraGroup.camera_group_id,
        label: cameraGroup.camera_group_name,
        areaList: cameraGroup.area_list,
        filterType:'area'
      }))
    ]
  }
})

/**
 * 選択中のカメラグループID（フィルタリング用）
 */
export const selectedCameraGroupIdState = atom({
  key: 'selectedCameraGroupIdState',
  default: selector({
    key: 'selectedCameraGroupIdStateSelector',
    get: ({get}) => {
      return getProperty(get(savedDisplayOperationState), `${WX_ID_LIVECAMERA}.cameraGroupId.value`) || null;
    }
  })
});

/**
 * 選択中のカメラグループ情報（フィルタリング用）
 */
export const selectedCameraGroupSelector = selector({
  key: 'selectedCameraGroupSelector',
  get: ({get}) => {
    const visible = get(weatherVisibleWithID(WX_ID_LIVECAMERA))
    if (!visible) {
      return undefined;
    }
    const list = get(cameraGroupListSelector);
    const id = get(selectedCameraGroupIdState);
    return list.find(group => group.id === id);
  }
});

/**
 * Contractに紐づくカメラリスト
 */
export const contractCameraQuery = selector({
  key: 'contractCameraQuery',
  get: ({get}) => {
    const {contract} = get(userState);
    const apiUrl = process.env.REACT_APP_CAMERA_API_URL;
    const apiKey = process.env.REACT_APP_CAMERA_API_KEY;
    return axios.get(`${apiUrl}/owner/selectable_list`, {
      params: {
        contract_id: contract
      },
      headers: {
        'x-api-key': apiKey
      },
      errorHandle: {
        message: COMMUNICATE_ERROR_MESSAGE_SELECTABLE_LIST,
        additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_RELOAD
      }
    }).then(res => {
      const map = new Map();
      res.data.selectable_list.forEach(camera => {
        if (!camera.nodisplay_flag) {
          map.set(camera.area, camera);
        }
      });
      //selectable_listにないけどselect_listにあるものはskip
      return res.data.select_list.reduce((accumulator, camera) => {
        if (!map.has(camera.area)) {
          return accumulator
        }
        accumulator.push(map.get(camera.area));
        return accumulator
      }, []).sort(cameraSort);
    }).catch(e => {
      console.error('contract camera list failed.', e);
      return [];
    });
  }
});

/**
 * カメラグループ内のカメラリスト
 */
export const groupCameraListFamily = atomFamily({
  key: 'groupCameraListFamily',
  default: selectorFamily({
    key: 'groupCameraListFamilySelector',
    get: (cameraGroupId) => ({get}) => {
      if (!cameraGroupId) {
        return [];
      }
      const list = get(cameraGroupListQuery);
      const group = list.find(group => group.camera_group_id === cameraGroupId);
      const contractCameraList = get(contractCameraQuery);
      const selectable = new Map();
      contractCameraList.forEach(c => selectable.set(c.area, c));
      return group.area_list.reduce((accumulator, area) => {
        if (selectable.has(area)) {
          accumulator.push(selectable.get(area));
        }
        return accumulator;
      },[]).sort(cameraSort);
    }
  })
});

/**
 * カメラグループの保存・削除
 */
export const useLiveCameraGroup = () => {
  const applyCameraGroup = useCallback((cameraGroup) => {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await executeLinksAPI('fosterlinks/livecamera_group', {
          method: 'post',
          data: {
            ...(cameraGroup.camera_group_id && {camera_group_id: cameraGroup.camera_group_id}),
            camera_group_name: cameraGroup.cameraGroupName,
            area_list: cameraGroup.selectedList.map(r => r.key),
          }
        });
        resolve(result);
      } catch (e) {
        console.error(e)
        reject({
          error: e,
          errorCode: e.response?.status
        });
      }
    });
  },[]);

  const deleteCameraGroup = useCallback((deleteCameraGroupIds) => {
    return new Promise(async (resolve, reject) => {
      try {
        await executeLinksAPI('fosterlinks/livecamera_group', {
          method: 'delete',
          data: {
            camera_group_ids: deleteCameraGroupIds
          }
        });
        resolve();
      } catch (e) {
        console.error(e)
        reject({
          error: e,
          errorCode: e.response?.status
        });
      }
    });
  }, []);

  return {
    applyCameraGroup,
    deleteCameraGroup
  }
};